import React from "react";
import { useTranslation } from "react-i18next";
import { Box, DropdownOption, FormGroup } from "@hawkins/components";
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckboxFormControl, DropdownFormControl, FormContextualErrors, FormProvider, InputFormControl, useForm } from "@hawkins/forms";
import { forwardRef, useImperativeHandle, useMemo } from "react";

import { blankRegistrationForm, TranslationNamespace, UserInfoForm } from "../../types";
import { UserInfoSchema } from '../../utils/schemas';
import { CountryDropDown } from "./CountryDropDown";
import { getStatesForCountryCode } from "../../utils/states";
import { useUserInfoStyles } from "./UserInfoForm.styles";
import { useSession } from "../../hooks/useSessionContext";
import { PhoneInputFormControl } from "../fields/inputs/PhoneInput";


export interface UserInfoProps {
  onSubmit: (formValues: UserInfoForm) => void;
  onFormStateChange?: (isValid: boolean, acceptTerms: boolean) => void;
}

export interface UserInfoRef {
  reset: () => void;
  submit: () => void;
}

export const UserInfo = forwardRef<UserInfoRef, UserInfoProps>(
  ({ onSubmit, onFormStateChange }, ref) => {
    const { t } = useTranslation(TranslationNamespace.LEP);
    const classes = useUserInfoStyles();
    const { userInfo } = useSession();

    const schema = UserInfoSchema(t);
    type FormSchema = z.infer<typeof schema>;
    const methods = useForm<FormSchema>({
      defaultValues: userInfo || blankRegistrationForm,
      mode: 'onChange',
      resolver: zodResolver(schema),
    });

    const { watch, formState } = methods;
    const acceptTerms = watch('acceptTerms');

    // Reset form when userInfo changes
    React.useEffect(() => {
      if (userInfo) {
        methods.reset(userInfo);
      }
    }, [userInfo, methods]);

    React.useEffect(() => {
      onFormStateChange?.(formState.isValid, acceptTerms);
    }, [formState.isValid, acceptTerms, onFormStateChange]);

    const handleSubmit = methods.handleSubmit(
      (formValues) => onSubmit(formValues as any),
      (error, evt) => console.info(error, evt),
    );

    useImperativeHandle(ref, () => ({
      reset: () => methods.reset(),
      submit: handleSubmit,
    }));

    const country = watch('agencyAddress.country');
    const stateOptions = useMemo(() => {
      if (!country) return [];
      return getStatesForCountryCode(country.value).map((state) => ({
        label: state.name,
        value: state.isoCode,
      }));
    }, [country]);

    const fieldLabels = useMemo(() => ({
      'givenName': t('req_fname'),
      'familyName': t('req_lname'),
      'emailAddress': t('email_field_desc2'),
      'phoneNumber': t('req_pnum'),
      'title': t('req_title'),
      'agencyName': t('req_agency'),
      'agencyAddress.street1': t('req_add_line1'),
      'agencyAddress.street2': t('req_add_line_2'),
      'agencyAddress.city': t('req_City'),
      'agencyAddress.zipCode': t('add-zip-code'),
      'agencyAddress.country': t('country_field_desc'),
      'agencyAddress.state': t('add_state'),
      'acceptTerms': t('certification_statement'),
    }), [t]);

    return (
      <FormProvider {...methods}>
        <FormGroup>
          <FormContextualErrors fieldLabels={fieldLabels} />
          <Box
            display="grid"
            gridAutoFlow="column"
            gridTemplateColumns={"1fr 1fr"}
            placeItems="flex-start"
          >
            <Box className={classes.formGridColumn}>
              <InputFormControl<FormSchema>
                label={fieldLabels.givenName}
                placeholder={fieldLabels.givenName}
                name="givenName"
                requirementType="required-star"
                tabIndex={1}
                data-testid="given-name-input"
              />
              <InputFormControl<FormSchema>
                label={fieldLabels.emailAddress}
                placeholder={fieldLabels.emailAddress}
                name="emailAddress"
                requirementType="required-star"
                tabIndex={3}
                data-testid="email-address-input"
              />
              <InputFormControl<FormSchema>
                label={fieldLabels.title}
                placeholder={fieldLabels.title}
                name="title"
                requirementType="required-star"
                tabIndex={5}
                data-testid="title-input"
              />
              <InputFormControl<FormSchema>
                label={fieldLabels['agencyAddress.street1']}
                placeholder={fieldLabels['agencyAddress.street1']}
                name="agencyAddress.street1"
                requirementType="required-star"
                tabIndex={7}
                data-testid="agency-address-street1-input"
              />
              <InputFormControl<FormSchema>
                label={fieldLabels['agencyAddress.city']}
                placeholder={fieldLabels['agencyAddress.city']}
                name="agencyAddress.city"
                requirementType="required-star"
                tabIndex={9}
                data-testid="agency-address-city-input"
              />
              <CountryDropDown<FormSchema>
                label={fieldLabels['agencyAddress.country']}
                placeholder={fieldLabels['agencyAddress.country']}
                name="agencyAddress.country"
                requirementType="required-star"
                tabIndex={11}
                onChange={() => { methods.setValue('agencyAddress.state', { label: '', value: '' }) }}
                data-testid="country-dropdown"
              />
            </Box>
            <Box className={classes.formGridColumn}>
              <InputFormControl<FormSchema>
                label={fieldLabels.familyName}
                placeholder={fieldLabels.familyName}
                name="familyName"
                requirementType="required-star"
                tabIndex={2}
                data-testid="family-name-input"
              />
              <PhoneInputFormControl<FormSchema>
                label={fieldLabels.phoneNumber}
                placeholder={"+X XXX-XXX-XXXX"}
                name="phoneNumber"
                requirementType="required-star"
                tabIndex={4}
                data-testid="phone-number-input"
              />
              <InputFormControl<FormSchema>
                label={fieldLabels.agencyName}
                placeholder={fieldLabels.agencyName}
                name="agencyName"
                requirementType="required-star"
                tabIndex={6}
                data-testid="agency-name-input"
              />
              <InputFormControl<FormSchema>
                label={fieldLabels['agencyAddress.street2']}
                placeholder={fieldLabels['agencyAddress.street2']}
                name="agencyAddress.street2"
                tabIndex={8}
                data-testid="agency-address-street2-input"
              />
              <InputFormControl<FormSchema>
                label={fieldLabels['agencyAddress.zipCode']}
                placeholder={fieldLabels['agencyAddress.zipCode']}
                name="agencyAddress.zipCode"
                requirementType="required-star"
                tabIndex={10}
                data-testid="agency-address-zip-code-input"
              />
              {stateOptions.length > 0 && (<DropdownFormControl<DropdownOption<string>, FormSchema>
                label={fieldLabels['agencyAddress.state']}
                placeholder={fieldLabels['agencyAddress.state']}
                name="agencyAddress.state"
                options={stateOptions}
                searchable
                sortOptions
                requirementType="required-star"
                tabIndex={12}
                data-testid="state-dropdown"
              />)}
            </Box>
          </Box>
          <CheckboxFormControl<FormSchema>
            label={t('checkbox')}
            name="acceptTerms"
            formFieldContainerProps={{ requirementType: 'required-star' }}
            tabIndex={13}
            data-testid="accept-terms-checkbox"
          />
        </FormGroup>
      </FormProvider>
    );
  }
)